// src/components/FinancialIntelligence/FIEntryCard.js
//
// Individual card in the FI thread history panel.
// Shows: date, trigger label, financialIntelligenceSummary (2 sentences), star indicator.

import React from 'react';
import { Box, Typography, Chip, IconButton } from '@mui/material';
import { Star as StarIcon, StarBorder as StarBorderIcon, Autorenew as StreamingIcon } from '@mui/icons-material';

const SITUATION_LABELS = {
  prepare_for_meeting:  'Pre-meeting brief',
  board_package:        'Board Package',
  understand_finances:  'Financial Health Diagnostic',
  decision_support:     'Decision Support',
  cash_flow_brief:      'Cash Flow Brief',
  cash_flow_deep_dive:  'Cash Flow Deep Dive',
  investor_readiness: 'Investor Readiness',
  forecast_intelligence: 'Forecast Intelligence',
  margin_pricing: 'Margin & Pricing',
  variance_brief: 'Variance Brief',
  freeform:             'Analysis',
};

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const FIEntryCard = ({ entry, isActive, isStreaming, onClick, onStarToggle }) => {
  const labelKey = entry.triggerLabel || entry.situationKey || entry.triggerType;
  const label = entry.reportTitle || SITUATION_LABELS[labelKey] || labelKey || 'Analysis';
  const summary = entry.financialIntelligenceSummary || '';
  const date = formatDate(entry.createdAt);

  return (
    <Box
      onClick={() => onClick(entry)}
      sx={{
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        borderLeft: '3px solid',
        borderLeftColor: isActive ? 'primary.main' : 'transparent',
        bgcolor: isActive ? 'primary.50' : 'transparent',
        '&:hover': { bgcolor: isActive ? 'primary.50' : 'action.hover' },
        transition: 'background-color 0.15s ease',
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
      }}
    >
      {/* Date + star row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.72rem' }}>
          {isStreaming ? 'Generating…' : date}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isStreaming && (
            <StreamingIcon sx={{ fontSize: 14, color: 'primary.main', animation: 'spin 1s linear infinite',
              '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } }
            }} />
          )}
          {!isStreaming && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onStarToggle && onStarToggle(entry); }}
              sx={{ p: 0.2, color: entry.starred ? 'warning.main' : 'text.disabled',
                '&:hover': { color: 'warning.main' } }}
            >
              {entry.starred
                ? <StarIcon sx={{ fontSize: 14 }} />
                : <StarBorderIcon sx={{ fontSize: 14 }} />
              }
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Trigger label */}
      <Typography
        variant="body2"
        fontWeight={isActive ? 600 : 500}
        sx={{ fontSize: '0.82rem', color: isActive ? 'primary.dark' : 'text.primary', mb: 0.4 }}
      >
        {label}
      </Typography>

      {/* Summary — 2 sentences max */}
      {summary && !isStreaming && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: '0.75rem',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
          }}
        >
          {summary}
        </Typography>
      )}

      {/* Data tier chip */}
      {entry.dataSnapshot?.financialIntelligenceTier && !isStreaming && (
        <Chip
          label={entry.dataSnapshot.financialIntelligenceTier === 'full' ? 'QB' :
                 entry.dataSnapshot.financialIntelligenceTier === 'questionnaire_only' ? 'Estimates' : 'No data'}
          size="small"
          sx={{ mt: 0.5, height: 16, fontSize: '0.65rem',
            bgcolor: entry.dataSnapshot.financialIntelligenceTier === 'full' ? 'success.50' : 'grey.100',
            color: entry.dataSnapshot.financialIntelligenceTier === 'full' ? 'success.dark' : 'text.secondary',
          }}
        />
      )}
    </Box>
  );
};

export default FIEntryCard;